import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import ModalBasicLayout from "./ModalBasicLayout";
import ContainerModalActions from "../Containers/ContainerModalActions";
import TextField from "../Form/TextField";
import TableContainer from "../Tables/TableContainer";
import TableCell from "../../pages/BillingAndPayments/components/TableCell";
import { useForm, useWatch } from "react-hook-form";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  startGetAdvertisersByAgency,
  startGetBrandsUsageAllocationData,
  startGetBusinessNameByAdvertiserIDs,
  startSetBrandUsageAllocations,
} from "../../actions/user";
import { SimpleAlert } from "../Alerts/Alerts";
import { PLAN_TYPE_CREDITS } from "../../utils/constants";
import { resolveBrandUsageAllocationKeysForCreditsPlan } from "../../utils/creditActionCosts";
import {
  ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_CREDITS_LABEL,
  ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_REMOVE_HINT,
  ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_TOTAL_ASSIGNED_LABEL,
  ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_PLAN_MAX_LABEL,
  ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_SUCCESS,
  ADVERTISER_SELECTOR_CONFIGURE_LIMITS_BUTTON,
  ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_VIDEOS_LABEL,
  ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_CONSUMED_LABEL,
  BRAND_USAGE_ALLOCATION_EXCEEDS_PLAN,
  BUTTON_CANCEL,
  BUTTON_SAVE,
  COLUMN_NAME_BRAND,
} from "../../i18n/keysTranslations";

const normalizeLimitFieldValue = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  if (typeof value === "object" && value?.target != null) {
    return normalizeLimitFieldValue(value.target.value);
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const parseLimitInput = (value) => {
  const parsed = normalizeLimitFieldValue(value);
  if (parsed === null || Number.isNaN(parsed)) {
    return null;
  }
  return parsed;
};

const validateLimitValue = (value) => {
  const parsed = normalizeLimitFieldValue(value);

  if (parsed === null) {
    return true;
  }

  return !Number.isNaN(parsed) && parsed >= 0;
};

const hasAssignedBrandUsageAllocation = (allocation, usageKey) =>
  allocation?.[usageKey] !== undefined && allocation?.[usageKey] !== null;

const hasBrandUsageConsumed = (consumed, usageKey) =>
  consumed?.[usageKey] !== undefined && consumed?.[usageKey] !== null;

const shouldShowBrandUsageConsumed = ({ allocation, consumed, usageKey }) =>
  hasAssignedBrandUsageAllocation(allocation, usageKey) &&
  hasBrandUsageConsumed(consumed, usageKey);

const getBrandUsageConsumedValue = (consumed, usageKey) =>
  Math.max(0, Number(consumed?.[usageKey]) || 0);

const buildDefaultValues = (brands, usageDataByBrand) => {
  const brandsValues = {};

  for (const brand of brands) {
    const allocation = usageDataByBrand[brand.id]?.allocation || {};
    brandsValues[brand.id] = {
      credits:
        allocation?.credits === undefined || allocation?.credits === null
          ? ""
          : String(allocation.credits),
      videos:
        allocation?.videos === undefined || allocation?.videos === null
          ? ""
          : String(allocation.videos),
    };
  }

  return { brands: brandsValues };
};

const sumAssignedLimits = (brandsFormValues, brands, usageKey) =>
  brands.reduce((total, brand) => {
    const parsed = parseLimitInput(brandsFormValues?.[brand.id]?.[usageKey]);
    return total + (parsed ?? 0);
  }, 0);

const validateTotalsWithinPlanMax = ({
  brandsFormValues,
  brands,
  showCreditsColumn,
  showVideosColumn,
  creditsPlanMax,
  videosPlanMax,
}) => {
  if (
    showCreditsColumn &&
    sumAssignedLimits(brandsFormValues, brands, "credits") > creditsPlanMax
  ) {
    return false;
  }
  if (
    showVideosColumn &&
    sumAssignedLimits(brandsFormValues, brands, "videos") > videosPlanMax
  ) {
    return false;
  }
  return true;
};

const sortAgencyBrands = (brands, agencyID) =>
  [...brands].sort((a, b) => {
    if (a.id === agencyID) return -1;
    if (b.id === agencyID) return 1;
    return (a.name || "").localeCompare(b.name || "");
  });

const ModalConfigureBrandLimits = ({ modalOpen, onCloseModal, agencyID }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const planBenefits = useSelectorApp(
    (state) => state.payments?.subscriptionPlanInfo?.benefits
  );
  const typeOfPlans = useSelectorApp((state) => state.payments?.typeOfPlans);
  const creditPricingVersion = useSelectorApp(
    (state) => state.payments?.creditPricingVersion
  );

  const [tableBrands, setTableBrands] = useState([]);

  const supportedKeys = useMemo(
    () => resolveBrandUsageAllocationKeysForCreditsPlan(creditPricingVersion),
    [creditPricingVersion]
  );

  const showCreditsColumn = supportedKeys.includes("credits");
  const showVideosColumn = supportedKeys.includes("videos");

  const [isLoadingBrandData, setIsLoadingBrandData] = useState(false);
  const [brandsUsageData, setBrandsUsageData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      brands: {},
    },
  });

  const watchedBrands = useWatch({
    control,
    name: "brands",
  });

  const creditsPlanMax = Number(planBenefits?.credits) || 0;
  const videosPlanMax = Number(planBenefits?.videos) || 0;

  const totalAssignedCredits = sumAssignedLimits(
    watchedBrands || {},
    tableBrands,
    "credits"
  );
  const totalAssignedVideos = sumAssignedLimits(
    watchedBrands || {},
    tableBrands,
    "videos"
  );
  const exceedsCreditsPlanMax =
    showCreditsColumn && totalAssignedCredits > creditsPlanMax;
  const exceedsVideosPlanMax =
    showVideosColumn && totalAssignedVideos > videosPlanMax;
  const hasTotalsExceedingPlanMax =
    exceedsCreditsPlanMax || exceedsVideosPlanMax;

  useEffect(() => {
    if (!modalOpen) {
      reset({ brands: {} });
      setBrandsUsageData({});
      setTableBrands([]);
      return;
    }

    let isCancelled = false;

    const loadModalData = async () => {
      setIsLoadingBrandData(true);
      try {
        const responseAdvertisers = await dispatch(
          startGetAdvertisersByAgency()
        );

        if (isCancelled || !responseAdvertisers?.ok) {
          return;
        }

        const advertiserIDs = (responseAdvertisers.data || []).filter(Boolean);
        if (advertiserIDs.length === 0) {
          setTableBrands([]);
          return;
        }

        const advertisersBusiness = await dispatch(
          startGetBusinessNameByAdvertiserIDs({ advertiserIDs })
        );

        if (isCancelled) {
          return;
        }

        const brands = sortAgencyBrands(
          advertiserIDs.map((advertiserID) => ({
            id: advertiserID,
            name: advertisersBusiness?.[advertiserID] || advertiserID,
          })),
          agencyID
        );

        setTableBrands(brands);

        const response = await dispatch(
          startGetBrandsUsageAllocationData(brands.map((brand) => brand.id))
        );

        if (isCancelled || !response?.ok) {
          return;
        }

        const usageDataByBrand = response.data || {};
        setBrandsUsageData(usageDataByBrand);
        reset(buildDefaultValues(brands, usageDataByBrand));
      } finally {
        if (!isCancelled) {
          setIsLoadingBrandData(false);
        }
      }
    };

    void loadModalData();

    return () => {
      isCancelled = true;
    };
  }, [agencyID, dispatch, modalOpen, reset]);

  const onSubmit = async (values) => {
    if (
      !validateTotalsWithinPlanMax({
        brandsFormValues: values.brands,
        brands: tableBrands,
        showCreditsColumn,
        showVideosColumn,
        creditsPlanMax,
        videosPlanMax,
      })
    ) {
      await SimpleAlert({
        title: t(BRAND_USAGE_ALLOCATION_EXCEEDS_PLAN),
        icon: "error",
      });
      return;
    }

    const brandsPayload = tableBrands.map((brand) => ({
      advertiserID: brand.id,
      limits: Object.fromEntries(
        supportedKeys.map((usageKey) => [
          usageKey,
          parseLimitInput(values.brands?.[brand.id]?.[usageKey]),
        ])
      ),
    }));

    setIsSubmitting(true);
    try {
      const response = await dispatch(
        startSetBrandUsageAllocations(brandsPayload)
      );
      if (!response?.ok) {
        return;
      }

      await SimpleAlert({
        title: t(ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_SUCCESS),
        icon: "success",
      });
      onCloseModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!modalOpen || typeOfPlans !== PLAN_TYPE_CREDITS) {
    return null;
  }

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(ADVERTISER_SELECTOR_CONFIGURE_LIMITS_BUTTON)}
      maxWidth="md"
      fullWidth
      showCloseButton={true}
    >
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
        <TableContainer
          loading={isLoadingBrandData}
          keys={tableBrands}
          hasPagination={false}
          ariaLabel="brand-usage-allocation"
          sxContainer={{ boxShadow: "none" }}
        >
          <TableHead>
            <TableRow>
              <TableCell isHeader={true} sx={{ pl: "16px !important" }}>
                {t(COLUMN_NAME_BRAND)}
              </TableCell>
              {showCreditsColumn ? (
                <TableCell isHeader={true}>
                  {t(ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_CREDITS_LABEL)}
                </TableCell>
              ) : null}
              {showVideosColumn ? (
                <TableCell isHeader={true}>
                  {t(ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_VIDEOS_LABEL)}
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableBrands.map((brand, index) => {
              const brandUsageData = brandsUsageData[brand.id] || {};
              const brandAllocation = brandUsageData.allocation || {};
              const brandConsumed = brandUsageData.consumed || {};

              return (
                <TableRow key={brand.id}>
                  <TableCell
                    indexRow={index}
                    isLastRow={false}
                    sx={{ pl: "16px !important", minWidth: 160 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {brand.name}
                    </Typography>
                  </TableCell>
                  {showCreditsColumn ? (
                    <TableCell
                      indexRow={index}
                      isLastRow={false}
                      sx={{ minWidth: 140 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 1 }}
                          label=""
                          name={`brands.${brand.id}.credits`}
                          variant="filled"
                          color="primary"
                          control={control}
                          rules={{
                            validate: validateLimitValue,
                          }}
                          errors={errors}
                          sx={{
                            minWidth: 120,
                            "& .MuiFilledInput-root": {
                              borderRadius: 1,
                            },
                          }}
                        />
                        {shouldShowBrandUsageConsumed({
                          allocation: brandAllocation,
                          consumed: brandConsumed,
                          usageKey: "credits",
                        }) ? (
                          <Typography variant="caption" color="text.secondary">
                            {t(
                              ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_CONSUMED_LABEL,
                              {
                                value: getBrandUsageConsumedValue(
                                  brandConsumed,
                                  "credits"
                                ),
                              }
                            )}
                          </Typography>
                        ) : null}
                      </Box>
                    </TableCell>
                  ) : null}
                  {showVideosColumn ? (
                    <TableCell
                      indexRow={index}
                      isLastRow={false}
                      sx={{ minWidth: 140 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 1 }}
                          label=""
                          name={`brands.${brand.id}.videos`}
                          variant="filled"
                          color="primary"
                          control={control}
                          rules={{
                            validate: validateLimitValue,
                          }}
                          errors={errors}
                          sx={{
                            minWidth: 120,
                            "& .MuiFilledInput-root": {
                              borderRadius: 1,
                            },
                          }}
                        />
                        {shouldShowBrandUsageConsumed({
                          allocation: brandAllocation,
                          consumed: brandConsumed,
                          usageKey: "videos",
                        }) ? (
                          <Typography variant="caption" color="text.secondary">
                            {t(
                              ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_CONSUMED_LABEL,
                              {
                                value: getBrandUsageConsumedValue(
                                  brandConsumed,
                                  "videos"
                                ),
                              }
                            )}
                          </Typography>
                        ) : null}
                      </Box>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell
                indexRow={tableBrands.length}
                isLastRow={false}
                sx={{ pl: "16px !important", minWidth: 160 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(
                    ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_TOTAL_ASSIGNED_LABEL
                  )}
                </Typography>
              </TableCell>
              {showCreditsColumn ? (
                <TableCell indexRow={tableBrands.length} isLastRow={false}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                    color={exceedsCreditsPlanMax ? "error" : "text.primary"}
                  >
                    {totalAssignedCredits}
                  </Typography>
                </TableCell>
              ) : null}
              {showVideosColumn ? (
                <TableCell indexRow={tableBrands.length} isLastRow={false}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                    color={exceedsVideosPlanMax ? "error" : "text.primary"}
                  >
                    {totalAssignedVideos}
                  </Typography>
                </TableCell>
              ) : null}
            </TableRow>
            <TableRow>
              <TableCell
                indexRow={tableBrands.length + 1}
                isLastRow={true}
                sx={{ pl: "16px !important", minWidth: 160 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_PLAN_MAX_LABEL)}
                </Typography>
              </TableCell>
              {showCreditsColumn ? (
                <TableCell indexRow={tableBrands.length + 1} isLastRow={true}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {creditsPlanMax}
                  </Typography>
                </TableCell>
              ) : null}
              {showVideosColumn ? (
                <TableCell indexRow={tableBrands.length + 1} isLastRow={true}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {videosPlanMax}
                  </Typography>
                </TableCell>
              ) : null}
            </TableRow>
          </TableBody>
        </TableContainer>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {t(ADMIN_DASHBOARD_BRAND_USAGE_ALLOCATION_REMOVE_HINT)}
          </Typography>
          {hasTotalsExceedingPlanMax ? (
            <Typography variant="caption" color="error" display="block">
              {t(BRAND_USAGE_ALLOCATION_EXCEEDS_PLAN)}
            </Typography>
          ) : null}
        </Box>

        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onCloseModal,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_SAVE,
            loading: isSubmitting,
            disabled: hasTotalsExceedingPlanMax || isLoadingBrandData,
            type: "button",
            onClick: handleSubmit(onSubmit),
          }}
        />
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalConfigureBrandLimits;
